import { BOOKING_URL } from '../../lib/constants';
import { Check } from 'lucide-react';

const SYSTEMS = [
  {
    flag: '🇫🇷',
    name: 'YUMI Keratin Lash Lift & Tint',
    tag: 'The Gold Standard',
    body:
      'The gold standard of lash lifts. YUMI (developed in France) uses a keratin-infused formula to lift, strengthen, and condition your lashes simultaneously. No harsh chemicals — just visibly longer, darker, healthier-looking lashes that last 8–12 weeks.',
    bestFor: 'All lash types, especially fine or straight lashes',
    duration: '75 minutes',
    includes: 'Lift + tint',
    price: 150,
    accent: true,
  },
  {
    flag: '🇮🇹',
    name: 'InLei Lash Filler® & Tint',
    tag: 'The Gentle Alternative',
    body:
      'A gentler, nourishing alternative made in Italy. InLei Lash Filler® is ideal for clients with fragile, over-processed, or shorter lashes. The filler formula adds volume and conditioning while lifting — your lashes feel healthier after every treatment.',
    bestFor: 'Sensitive eyes or fragile lashes',
    duration: '75 minutes',
    includes: 'Lift + tint',
    price: 125,
    accent: false,
  },
];

const COMPARISON_ROWS = [
  ['Origin', 'France', 'Italy'],
  ['Formula', 'Keratin-infused', 'Filler + conditioning'],
  ['Best for', 'All lash types', 'Fine / fragile lashes'],
  ['Lasts', '8–12 weeks', '6–8 weeks'],
  ['Price', '$150', '$125'],
];

export const SystemsComparison = () => {
  return (
    <section
      id="systems"
      data-testid="systems-section"
      className="section-pad relative overflow-hidden bg-brand-bg"
    >
      <div aria-hidden className="absolute inset-0 bg-brand-gradient-soft pointer-events-none" />
      <div className="container-prep relative">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="eyebrow mb-5">The Two Treatments</p>
          <h2 data-testid="systems-h2" className="heading-2 mb-6">
            Two premium lash lift systems.
            <span className="italic block text-brand-gold">One studio.</span>
          </h2>
          <p className="body-lg">
            Most lash lift studios in Vancouver offer one brand. We offer two —
            so we can match the right treatment to your lashes, lifestyle, and budget.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {SYSTEMS.map((s) => (
            <article
              key={s.name}
              data-testid={`system-card-${s.name.split(' ')[0].toLowerCase()}`}
              className={`group relative bg-brand-bgElev rounded-3xl p-8 sm:p-10 border transition-all duration-500 hover:-translate-y-1 ${
                s.accent
                  ? 'border-brand-gold/40 shadow-[0_20px_60px_-30px_rgba(201,169,110,0.6)]'
                  : 'border-brand-gold/15 hover:border-brand-gold/30 hover:shadow-[0_20px_60px_-30px_rgba(201,169,110,0.35)]'
              }`}
            >
              {s.accent && (
                <div className="absolute -top-3 left-8 px-3 py-1 bg-brand-gold text-brand-ink text-[10px] uppercase tracking-[0.22em] rounded-full font-semibold">
                  Bestseller
                </div>
              )}
              <div className="flex items-center justify-between mb-5">
                <span className="text-3xl" aria-hidden>{s.flag}</span>
                <span className="eyebrow">{s.tag}</span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-brand-cream mb-4 leading-snug">{s.name}</h3>
              <p className="text-brand-muted leading-relaxed mb-6">{s.body}</p>

              <ul className="space-y-2.5 mb-8">
                <li className="flex items-start gap-3 text-sm text-brand-cream/85">
                  <Check size={16} className="text-brand-gold mt-0.5 shrink-0" />
                  <span><span className="text-brand-cream font-medium">Best for:</span> {s.bestFor}</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-cream/85">
                  <Check size={16} className="text-brand-gold mt-0.5 shrink-0" />
                  <span><span className="text-brand-cream font-medium">Duration:</span> {s.duration}</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-cream/85">
                  <Check size={16} className="text-brand-gold mt-0.5 shrink-0" />
                  <span><span className="text-brand-cream font-medium">Includes:</span> {s.includes}</span>
                </li>
              </ul>

              <div className="flex items-end justify-between pt-6 border-t border-brand-gold/15">
                <div>
                  <div className="eyebrow mb-1 text-brand-muted">Starting at</div>
                  <div className="font-serif text-5xl text-brand-cream leading-none">
                    ${s.price}
                  </div>
                </div>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`system-book-${s.name.split(' ')[0].toLowerCase()}`}
                  className="btn-primary !px-5 !py-3 text-xs"
                >
                  Book →
                </a>
              </div>
            </article>
          ))}
        </div>

        {/* Comparison table */}
        <div className="mt-16 max-w-3xl mx-auto">
          <p className="eyebrow text-center mb-6">Side by Side</p>
          <div className="bg-brand-bgElev rounded-3xl border border-brand-gold/15 overflow-hidden">
            <div className="bg-brand-bgSoft grid grid-cols-3 px-6 py-4 text-[11px] uppercase tracking-[0.2em] border-b border-brand-gold/15">
              <div></div>
              <div className="text-center text-brand-gold font-semibold">YUMI Keratin</div>
              <div className="text-center text-brand-gold font-semibold">InLei Lash Filler®</div>
            </div>
            {COMPARISON_ROWS.map(([label, a, b], i) => (
              <div
                key={label}
                className={`grid grid-cols-3 px-6 py-4 text-sm ${
                  i !== COMPARISON_ROWS.length - 1 ? 'border-b border-brand-gold/10' : ''
                }`}
              >
                <div className="font-medium text-brand-cream">{label}</div>
                <div className="text-center text-brand-muted">{a}</div>
                <div className="text-center text-brand-muted">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SystemsComparison;
